/**
 * Client-side image compression for support attachments.
 *
 * Why this exists at all: the images people attach to support tickets are
 * screenshots, and a modern screenshot is a 3–8 MB PNG of mostly flat colour. Sent
 * as-is, five of them is 40 MB through somebody's hotel wifi to prove that a
 * command printed the wrong thing. Re-encoded, the same five are under 2 MB and
 * look identical, because a screenshot is exactly the kind of image that survives
 * this well.
 *
 * The two decisions that matter:
 *
 * WebP, falling back to JPEG. WebP at quality 0.82 beats JPEG at the same visual
 * quality by a wide margin on screenshots, and unlike JPEG it does not put ringing
 * artefacts around text — which on a screenshot of a terminal is the entire content.
 * The fallback is for the rare browser without WebP encoding, detected by asking the
 * canvas rather than by sniffing a user agent.
 *
 * A size target, approached by lowering quality and then dimensions. A fixed quality
 * cannot promise a size, and the upload has a hard cap: exceeding it means the
 * signed PUT is refused after the bytes have already been sent. So this loop trades
 * quality down first — invisible on a screenshot — and only then scales the image,
 * because losing pixels is what actually makes text unreadable.
 */

/** What the API accepts. Kept in step with support.AllowedImageTypes on the server. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const

/** The largest edge a compressed screenshot keeps. 2560 preserves a Retina window
 *  at 1:1 for the width people actually screenshot, and halves a 5K capture. */
const MAX_EDGE = 2560

/** Quality ladder, tried in order until the result fits the target. Stops at 0.55:
 *  below that a screenshot's text starts to smear, and a smaller file that cannot
 *  be read is not a smaller file, it is a wasted upload. */
const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.55] as const

/** Scale ladder, tried only after quality is exhausted. */
const SCALE_STEPS = [1, 0.8, 0.65, 0.5] as const

export interface CompressedImage {
  /** The bytes to upload. */
  blob: Blob
  /** The name to show on the ticket, with the extension corrected to what we encoded. */
  filename: string
  contentType: string
  width: number
  height: number
  /** The original size, so the UI can show what the compression saved. */
  originalBytes: number
}

export class ImageError extends Error {}

let webpSupport: boolean | undefined

/**
 * Asks the canvas whether it can encode WebP.
 *
 * `toDataURL` returns a PNG data URL when the requested type is unsupported, so the
 * answer is in the prefix of what comes back. Cached because the answer cannot
 * change within a page load.
 */
function supportsWebP(): boolean {
  if (webpSupport === undefined) {
    const probe = document.createElement('canvas')
    probe.width = 1
    probe.height = 1
    webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp')
  }
  return webpSupport
}

function extensionFor(contentType: string): string {
  switch (contentType) {
    case 'image/webp':
      return '.webp'
    case 'image/png':
      return '.png'
    default:
      return '.jpg'
  }
}

/** Replaces whatever extension a file had with the one we actually encoded. A
 *  screenshot named `Screenshot.png` that is now WebP should not claim to be a PNG. */
function renameTo(original: string, contentType: string): string {
  const base = original.replace(/\.[^./\\]+$/, '') || 'screenshot'
  return base + extensionFor(contentType)
}

/**
 * Decodes a file into a bitmap.
 *
 * `createImageBitmap` where available, because it decodes off the main thread and a
 * 8 MP PNG decoded synchronously is a visible freeze. The <img> path is the fallback,
 * and it revokes its object URL on both outcomes — a leaked blob URL holds the whole
 * decoded image for the life of the page.
 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file)
    } catch {
      // Fall through: some browsers refuse specific colour profiles here but load
      // the same file happily through an <img>.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new ImageError('That file could not be read as an image.'))
      image.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

function toBlob(canvas: HTMLCanvasElement, contentType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImageError('That image could not be compressed.'))),
      contentType,
      quality,
    )
  })
}

/**
 * Compresses one image to fit `targetBytes`.
 *
 * Returns the smallest acceptable result rather than throwing when the target
 * cannot be met at the bottom of both ladders — the caller compares against the
 * hard cap and reports it, which produces one clear message about one file instead
 * of a generic failure for the whole selection.
 */
export async function compressImage(file: File, targetBytes: number): Promise<CompressedImage> {
  if (!file.type.startsWith('image/')) {
    throw new ImageError(`${file.name} is not an image.`)
  }

  const bitmap = await decode(file)
  const sourceWidth = 'width' in bitmap ? bitmap.width : 0
  const sourceHeight = 'height' in bitmap ? bitmap.height : 0
  if (!sourceWidth || !sourceHeight) {
    throw new ImageError(`${file.name} could not be read as an image.`)
  }

  const contentType = supportsWebP() ? 'image/webp' : 'image/jpeg'
  // The longest edge is capped before the ladders start, so a 5K screenshot begins
  // from a sane size rather than spending the whole quality ladder on pixels nobody
  // will look at.
  const capScale = Math.min(1, MAX_EDGE / Math.max(sourceWidth, sourceHeight))

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    throw new ImageError('This browser cannot compress images.')
  }

  let best: { blob: Blob; width: number; height: number } | undefined

  for (const scale of SCALE_STEPS) {
    const width = Math.max(1, Math.round(sourceWidth * capScale * scale))
    const height = Math.max(1, Math.round(sourceHeight * capScale * scale))

    canvas.width = width
    canvas.height = height
    // A JPEG has no alpha, so an unpainted background composites to black and a
    // screenshot with rounded corners or a transparent title bar arrives with black
    // wedges. Filling white first is what avoids that.
    if (contentType === 'image/jpeg') {
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, width, height)
    } else {
      context.clearRect(0, 0, width, height)
    }
    context.drawImage(bitmap as CanvasImageSource, 0, 0, width, height)

    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, contentType, quality)
      if (!best || blob.size < best.blob.size) {
        best = { blob, width, height }
      }
      if (blob.size <= targetBytes) {
        if ('close' in bitmap) bitmap.close()
        return {
          blob,
          filename: renameTo(file.name, contentType),
          contentType,
          width,
          height,
          originalBytes: file.size,
        }
      }
    }
  }

  if ('close' in bitmap) bitmap.close()
  if (!best) {
    throw new ImageError(`${file.name} could not be compressed.`)
  }
  return {
    blob: best.blob,
    filename: renameTo(file.name, contentType),
    contentType,
    width: best.width,
    height: best.height,
    originalBytes: file.size,
  }
}

/** Formats a byte count for the attachment list. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
